import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { useRealtimeEvent } from "../context/RealtimeContext";
import { api, fmtDate, formatErr } from "../lib/api";
import { Empty, Field, PageHeader, inputCls } from "./kit";
import { PasswordInput } from "../components/site/PasswordInput";
import { PLATFORMS } from "../components/site/QuoteFormDynamic";

export function Notifications() {
  const nav = useNavigate();
  const [d, setD] = useState({ items: [], unread: 0 });
  const load = useCallback(() => api.get("/notifications").then((r) => setD(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);
  useRealtimeEvent((m) => {
    if (m.event === "notification") load();
  });
  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    if (n.link) nav(n.link);
    else load();
  };
  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" desc={`${d.unread} unread`}>
        <button className="btn-ghost !py-2" onClick={async () => { await api.post("/notifications/read-all"); load(); }} data-testid="notifications-page-mark-all">Mark all read</button>
      </PageHeader>
      {d.items.length === 0 ? <Empty text="No notifications yet." /> : (
        <div className="card-neon !p-0 divide-y divide-white/5">
          {d.items.map((n) => (
            <button key={n.id} onClick={() => open(n)} className={`w-full text-left p-4 hover:bg-white/5 flex gap-3 ${n.read ? "opacity-60" : ""}`} data-testid={`notifications-row-${n.id}`}>
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-slate-600" : "bg-[#00d9ff]"}`} />
              <div className="flex-1 min-w-0"><p className="font-semibold text-white">{n.title}</p>{n.body && <p className="text-sm text-slate-400">{n.body}</p>}</div>
              <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(n.created_at)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name, gamertag: user.gamertag || "", platform: user.platform || "", phone: user.phone || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const save = async (body) => {
    try {
      setUser((await api.put("/auth/profile", body)).data);
      toast.success("Profile saved");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div data-testid="profile-page" className="max-w-2xl">
      <PageHeader title="Profile Settings" desc={user.email} />
      <div className="card-neon p-6 grid sm:grid-cols-2 gap-4">
        <Field label="Display name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="profile-name-input" /></Field>
        <Field label="Gamertag / PSN ID"><Input value={f.gamertag} onChange={(e) => setF({ ...f, gamertag: e.target.value })} className={inputCls} data-testid="profile-gamertag-input" /></Field>
        <Field label="Main platform"><Input list="platforms" value={f.platform} onChange={(e) => setF({ ...f, platform: e.target.value })} className={inputCls} data-testid="profile-platform-input" /><datalist id="platforms">{PLATFORMS.map((p) => <option key={p} value={p} />)}</datalist></Field>
        <Field label="Phone / Discord"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="profile-phone-input" /></Field>
        <div className="sm:col-span-2 flex gap-2"><button onClick={() => save(f)} className="btn-electric !py-2" data-testid="profile-save-btn">Save</button><button onClick={() => setF({ name: user.name, gamertag: user.gamertag || "", platform: user.platform || "", phone: user.phone || "" })} className="btn-ghost !py-2">Cancel</button></div>
      </div>
      <div className="card-neon p-6 mt-6 grid sm:grid-cols-2 gap-4">
        <h2 className="sm:col-span-2 font-cond font-bold uppercase tracking-wider">Change password</h2>
        <Field label="Current password"><PasswordInput value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} testid="profile-current-pw" /></Field>
        <Field label="New password"><PasswordInput value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} testid="profile-new-pw" /></Field>
        <div className="sm:col-span-2"><button onClick={() => save(pw)} className="btn-ghost !py-2" data-testid="profile-pw-save">Update password</button></div>
      </div>
    </div>
  );
}
